import React, { useMemo, useCallback, useEffect } from 'react';
import Avatar from '@atlaskit/avatar';
import { Grid, Text, Flex, xcss, Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIsFullPageIssueView } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { IdeaViewBentoSidebarEntryPoint } from '@atlassian/jira-polaris-component-idea-view-bento-sidebar/entrypoint.tsx';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	DRAWER,
} from '@atlassian/jira-product-analytics-bridge';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useHoverState } from '../../common/ui/hover-state/index.tsx';
import {
	useActiveEntryPointSubjectActions,
	useOpenedOnce,
} from '../../controllers/entrypoint-reference-store.tsx';
import { IdeaUnlinkCrossButton } from './idea-unlink-cross-button/index.tsx';

export const IdeaCard = ({
	summary,
	issueKey,
	issueId,
	issueTypeIconUrl,
	issueTypeName,
	issueLinkId,
	isUrlSelected,
	onUnlinkModalConfirmed,
}: {
	summary: string;
	issueKey: string;
	issueId: string;
	issueTypeIconUrl?: string;
	issueTypeName?: string;
	issueLinkId?: string;
	isUrlSelected?: boolean;
	onUnlinkModalConfirmed: (issueLinkId: string) => void;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isFullPageIssueView: boolean = useIsFullPageIssueView();
	const cloudId = useCloudId();
	const [isHovered, hoverProps] = useHoverState();
	const { setEntryPointReferenceSubject } = useActiveEntryPointSubjectActions();
	const [openedOnce] = useOpenedOnce();

	const entryPointParams = useMemo(() => ({ issueKey, cloudId }), [cloudId, issueKey]);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		IdeaViewBentoSidebarEntryPoint,
		entryPointParams,
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions);

	const handleOnClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.preventDefault();
			e.stopPropagation();

			if (isFullPageIssueView) {
				setEntryPointReferenceSubject(entryPointReferenceSubject);
			} else {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(`/browse/${issueKey}`, '_blank');
			}

			fireUIAnalytics(createAnalyticsEvent({}), 'card clicked', 'ideaLinkedCard', {
				target: isFullPageIssueView ? 'sidebar' : 'newTab',
				clickedIssueId: issueId,
			});
		},
		[
			issueKey,
			createAnalyticsEvent,
			isFullPageIssueView,
			issueId,
			setEntryPointReferenceSubject,
			entryPointReferenceSubject,
		],
	);

	/* will not cause endless loop because of the `isUrlSelected` and `openedOnce` dependency */
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isUrlSelected && !openedOnce) {
			entryPointActions.load();
			setEntryPointReferenceSubject(entryPointReferenceSubject);
		}
	}, [
		entryPointActions,
		entryPointReferenceSubject,
		isUrlSelected,
		openedOnce,
		setEntryPointReferenceSubject,
		createAnalyticsEvent,
	]);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			testId="issue-view-jpd-ideas.ui.idea-card.idea-card"
			ref={triggerRef}
			onClick={handleOnClick}
			xcss={ideaCardStyles}
			onMouseOver={hoverProps.onMouseOver}
			onFocus={hoverProps.onFocus}
			onMouseOut={hoverProps.onMouseOut}
			onBlur={hoverProps.onBlur}
		>
			{isUrlSelected && !openedOnce && (
				<ContextualAnalyticsData
					sourceName="ideaViewBentoSidebarWithOnboardingIdeaKey"
					sourceType={DRAWER}
					attributes={{
						issueKey,
						issueId,
						issueLinkId,
					}}
				>
					<FireScreenAnalytics />
				</ContextualAnalyticsData>
			)}
			<Grid templateColumns={issueTypeIconUrl ? '20px 1fr' : '1fr'} gap="space.100">
				{issueTypeIconUrl ? (
					<Tooltip content={issueTypeName || null}>
						<Flex>
							<Avatar
								appearance="square"
								src={issueTypeIconUrl}
								size="xsmall"
								borderColor="transparent"
							/>
						</Flex>
					</Tooltip>
				) : null}
				<Text maxLines={1}>{summary}</Text>
			</Grid>
			{issueLinkId && (
				<IdeaUnlinkCrossButton
					issueKey={issueKey}
					issueLinkId={issueLinkId}
					isParentHovered={isHovered}
					onUnlinkModalConfirmed={onUnlinkModalConfirmed}
				/>
			)}
		</Box>
	);
};

const ideaCardStyles = xcss({
	textDecoration: 'none',
	background: 'none',
	cursor: 'pointer',

	border: 'solid',
	borderColor: 'color.border',
	borderWidth: '0px',

	borderBottomWidth: 'border.width',
	borderLeftWidth: 'border.width',
	borderRightWidth: 'border.width',

	paddingLeft: 'space.100',
	paddingRight: 'space.100',

	display: 'flex',
	alignItems: 'center',
	height: '40px',
	justifyContent: 'space-between',

	transition: '0.3s',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		color: 'color.text',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	':first-of-type': {
		borderTopWidth: 'border.width',
		borderTopLeftRadius: 'radius.small',
		borderTopRightRadius: 'radius.small',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		borderBottomWidth: 'border.width',
		borderBottomLeftRadius: 'radius.small',
		borderBottomRightRadius: 'radius.small',
	},
});
